import { message } from 'antd';
import { RecordInfoDrawer } from 'components/RecordInfoDrawer';
import { TableHeader } from 'components/TableHeader';
import {
  DELETE_ERROR,
  FETCH_ERROR,
  REQUEST_LOADING,
  SUCCESS_DELETE,
} from 'constants/alerts';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import { useDrawers } from 'hooks/useDrawers';
import { useHeaderChanger } from 'hooks/useHeaderChanger';
import { usePagination } from 'hooks/usePagination';
import React, { useEffect, useState } from 'react';
import { CalendarService } from 'services/CalendarService';
import { boolToText, formatDate } from 'utils';
import { HandbookCalendarForm } from './HandbookCalendarForm';
import { HandbookCalendarTable } from './HandbookCalendarTable';

function HandbookCalendar() {
  useDocumentTitle('Календарь');
  const [calendarItems, setCalendarItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const { setCount } = useHeaderChanger('Календарь');
  const { page, totalRows, perPage, setTotalRows, setPagination } =
    usePagination();
  const {
    state: { drawerVisible, formIsActive, activeRecord },
    openDrawer,
    closeDrawer,
    openCreateForm,
    openEditForm,
    closeForm,
  } = useDrawers();

  useEffect(() => {
    fetchCalendar();
  }, [page, perPage]);

  const fetchCalendar = async () => {
    if (!page || !perPage) {
      return;
    }

    setLoading(true);

    try {
      const { items, total } = await CalendarService.getCalendar(page, perPage);

      setCount(total);
      setCalendarItems(items);
      setTotalRows(total);
    } catch (err) {
      message.error(FETCH_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isDeleted = await CalendarService.deleteCalendar(activeRecord?.id);

      if (isDeleted) {
        closeDrawer();
        fetchCalendar();
        message.success(SUCCESS_DELETE);
      }
    } catch (err) {
      message.error(DELETE_ERROR);
    } finally {
      hide();
    }
  };

  return (
    <>
      <TableHeader onCreate={openCreateForm} pageId="calendar" />

      <HandbookCalendarTable
        items={calendarItems}
        loading={loading}
        current={page}
        pageSize={perPage}
        totalRows={totalRows}
        onRowClick={openDrawer}
        onChange={setPagination}
      />

      <HandbookCalendarForm
        isActive={formIsActive}
        initialValues={activeRecord}
        onSuccess={fetchCalendar}
        onClose={closeForm}
      />

      <RecordInfoDrawer
        record={activeRecord}
        drawerTitle={activeRecord?.name}
        recordFields={[
          {
            label: 'Дата',
            key: 'date',
            computedValue: formatDate(activeRecord?.date),
          },
          { label: 'Наименование', key: 'name' },
          { label: 'Описание', key: 'description' },
          { label: 'Описание (каз.)', key: 'description_kz' },
          {
            label: 'Рабочий день 5',
            key: 'work_day_5',
            computedValue: boolToText(activeRecord?.work_day_5),
          },
          {
            label: 'Рабочий день 6',
            key: 'work_day_6',
            computedValue: boolToText(activeRecord?.work_day_6),
          },
          {
            label: 'Выходной',
            key: 'holiday',
            computedValue: boolToText(activeRecord?.holiday),
          },
          {
            label: 'Государственный праздник',
            key: 'public_holiday',
            computedValue: boolToText(activeRecord?.public_holiday),
          },
          {
            label: 'Пометка',
            key: 'remark',
            computedValue: boolToText(activeRecord?.remark),
          },
          {
            label: 'Отображать в мобилке',
            key: 'need_to_mobile',
            computedValue: boolToText(activeRecord?.need_to_mobile),
          },
        ]}
        isVisible={drawerVisible}
        onClose={closeDrawer}
        onEdit={openEditForm}
        onDelete={handleDelete}
        deleteConfirmText="Вы точно хотите удалить эту запись?"
        pageId="calendar"
      />
    </>
  );
}

export { HandbookCalendar };
